import { SPEND_KPIS, getPerson } from '@/demo/procurement/data/company';
import { fmtNumber } from '@/demo/procurement/data/format';
import type { AgentDef, AgentId, Guardrail } from './agents-types';
import { AGENT_IDS } from './agents-types';

function millions(n: number, digits = 2): string {
  return `$${(n / 1_000_000).toFixed(digits)}M`;
}

function limit(
  id: string,
  before: string,
  unit: 'usd' | 'pct' | 'num',
  value: number,
  min: number,
  max: number,
  after?: string,
): Guardrail {
  return {
    id,
    before,
    after,
    control: { type: 'limit', unit, value, min, max },
  };
}

function rule(id: string, before: string, enabled = true): Guardrail {
  return { id, before, control: { type: 'rule', enabled } };
}

function hard(id: string, before: string): Guardrail {
  return { id, before, control: { type: 'hard' } };
}

/** Every agent except the coordinator. */
const COORDINATED_IDS: AgentId[] = AGENT_IDS.filter((id) => id !== 'director');

export const AGENTS: AgentDef[] = [
  {
    id: 'director',
    name: 'Procurement director',
    short:
      'Assigns work to the other agents, settles handoffs, answers questions, escalates to people',
    description: `Coordinates the other ${COORDINATED_IDS.length} agents. It reads every alert, request and handoff, assigns each piece of work to the agent whose procedure covers it, settles disagreements between agents and answers the team's questions in plain language. It never spends money, signs or sends anything itself. A decision that needs a person goes to a named approver with the reasons and numbers attached. Every assignment is posted in the team channel.`,
    channels: ['teams', 'erp'],
    defaultAutonomy: 'autonomous',
    defaultStatus: 'active',
    sopId: 'PRC-SOP-001',
    metrics: {
      actions: 2_960,
      acceptancePct: 97.4,
      hoursSaved: 130,
      approvalsRequired: 190,
      impact: { value: '410', label: 'handoffs settled without a person' },
    },
    ytd: [
      { label: 'Tasks assigned', value: '17,900' },
      { label: 'Handoffs settled', value: '4,900' },
      { label: 'Questions answered', value: '3,400' },
      { label: 'Escalations to people', value: '1,800' },
    ],
    where: [
      {
        label: 'Team channel',
        ref: { kind: 'page', page: 'agents-channel', label: 'Team channel' },
      },
      {
        label: 'Supplier risk',
        ref: { kind: 'page', page: 'risk', label: 'Supplier risk' },
      },
      {
        label: 'Requests',
        ref: { kind: 'page', page: 'requests', label: 'Requests' },
      },
    ],
    guardrails: [
      hard(
        'director:no-spend',
        'Never approve spend, sign or send anything itself; each agent acts inside its own limits',
      ),
      hard(
        'director:own-lane',
        'Hand each task to the agent whose procedure covers it; never do another agent’s work',
      ),
      limit(
        'director:ask-person',
        'Ask a person to approve any decision that commits more than',
        'usd',
        25_000,
        5_000,
        100_000,
      ),
      limit(
        'director:stalled',
        'Escalate a handoff that no agent has picked up after',
        'num',
        30,
        10,
        120,
        'minutes',
      ),
      rule(
        'director:conflicts',
        'When two agents disagree, follow the stricter policy and post the reason in the team channel',
      ),
      hard(
        'director:visible-data',
        'Answer a question only from data the person is allowed to see',
      ),
    ],
    coordinates: COORDINATED_IDS,
  },
  {
    id: 'intake',
    name: 'Intake agent',
    short:
      'Classifies requests, checks duplicates and policy, routes approvals',
    description:
      'Takes requests in plain language from Teams, email and the request form. Classifies each one, finds existing contracts and catalog items, runs the duplicate check, applies spend policy and routes approvals. The compliance review is done before the approver opens the request.',
    channels: ['teams', 'email'],
    defaultAutonomy: 'autonomous',
    defaultStatus: 'active',
    sopId: 'PRC-SOP-003',
    metrics: {
      actions: 1_840,
      acceptancePct: 96.2,
      hoursSaved: 210,
      approvalsRequired: 270,
      impact: { value: '41', label: 'duplicate requests stopped' },
    },
    ytd: [
      {
        label: 'Intake to PO',
        value: `${SPEND_KPIS.intakeToPoDays} days (was ${SPEND_KPIS.intakeToPoDaysBefore})`,
      },
      { label: 'Open requests', value: String(SPEND_KPIS.openRequests) },
      { label: 'Duplicates stopped', value: '312' },
    ],
    where: [
      {
        label: 'Requests',
        ref: { kind: 'page', page: 'requests', label: 'Requests' },
      },
    ],
    guardrails: [
      limit(
        'intake:auto-approve',
        'Auto-approve on-contract requests within budget up to',
        'usd',
        75_000,
        10_000,
        250_000,
      ),
      limit(
        'intake:bids',
        'Require three competitive bids above',
        'usd',
        250_000,
        100_000,
        1_000_000,
      ),
      rule(
        'intake:duplicates',
        'Run the duplicate check against open requests and POs before routing',
      ),
      rule(
        'intake:category-owner',
        'Route direct-material requests to the category owner first',
      ),
      hard(
        'intake:no-approve',
        'Never approve a request on behalf of an approver',
      ),
    ],
  },
  {
    id: 'onboarding',
    name: 'Supplier onboarding agent',
    short: 'Collects documents, verifies bank and credentials, screens risk',
    description:
      'Runs supplier qualification end to end: collects documents, verifies tax and bank ownership, scores credit, screens sanctions, adverse media and litigation, checks UL listings, CE marking (the European safety mark), ISO 9001 and insurance, then prepares the approval packet for the approvers.',
    channels: ['email', 'portal', 'phone'],
    defaultAutonomy: 'approval',
    defaultStatus: 'active',
    sopId: 'SQA-SOP-021',
    metrics: {
      actions: 610,
      acceptancePct: 94.5,
      hoursSaved: 120,
      approvalsRequired: 232,
      impact: { value: '2', label: 'bank-detail mismatches caught' },
    },
    ytd: [
      { label: 'Active cases', value: '3' },
      { label: 'Cases completed', value: '58' },
      { label: 'Avg days to approval', value: '11 (was 34)' },
    ],
    where: [
      {
        label: 'Onboarding ONB-0087',
        ref: { kind: 'onboarding', id: 'ONB-0087' },
      },
    ],
    guardrails: [
      hard('onboarding:no-approve', 'Never approve a supplier'),
      hard(
        'onboarding:bank-callback',
        'Bank changes require callback verification',
      ),
      hard(
        'onboarding:bank-ownership',
        'Verify account ownership before bank details are saved',
      ),
      limit(
        'onboarding:chase',
        'Chase missing documents at most',
        'num',
        3,
        1,
        6,
        'times, then escalate to the buyer',
      ),
      rule(
        'onboarding:screening-hold',
        'Hold the case when a sanctions or adverse-media hit needs review',
      ),
    ],
  },
  {
    id: 'contracts',
    name: 'Contract intelligence agent',
    short:
      'Extracts clauses, answers questions, tracks renewals and obligations',
    description:
      'Reads every agreement and exhibit, extracts clauses with section citations, answers questions with sources, and tracks renewals, notice deadlines and obligations so nothing auto-renews unnoticed.',
    channels: ['email', 'teams'],
    defaultAutonomy: 'autonomous',
    defaultStatus: 'active',
    sopId: 'LEG-SOP-009',
    metrics: {
      actions: 3_120,
      acceptancePct: 97.8,
      hoursSaved: 190,
      approvalsRequired: 110,
      impact: { value: '38', label: 'renewals and notice deadlines flagged' },
    },
    ytd: [
      { label: 'Active agreements', value: '1,946' },
      { label: 'Clauses extracted', value: '41,580' },
      { label: 'Renewals in 90 days', value: '38' },
    ],
    where: [
      {
        label: 'Contracts',
        ref: { kind: 'page', page: 'contracts', label: 'Contracts' },
      },
    ],
    guardrails: [
      rule(
        'contracts:auto-extract',
        'Extract clauses automatically from newly uploaded agreements',
      ),
      limit(
        'contracts:notice-window',
        'Flag renewals and notice deadlines',
        'num',
        90,
        30,
        180,
        'days ahead',
      ),
      hard(
        'contracts:cite-only',
        'Answer only with a cited clause; decline when no clause supports the answer',
      ),
      rule(
        'contracts:legal-review',
        `Route contract wording changes and legal questions to ${getPerson('elena-novak').name}, Senior Counsel`,
      ),
    ],
  },
  {
    id: 'compliance',
    name: 'Invoice compliance agent',
    short: 'Checks every invoice against contract clauses before payment',
    description:
      'Checks every supplier invoice against the governing agreement before payment: payment terms, unit price and price increase caps, freight and duty, agreed hourly rates, budget caps, surcharge caps, uptime credits and volume rebates. Disputes overbilling, pays less than billed where justified and schedules payment on contract terms.',
    channels: ['email', 'erp'],
    defaultAutonomy: 'approval',
    defaultStatus: 'active',
    sopId: 'AP-SOP-014',
    metrics: {
      actions: 4_380,
      acceptancePct: 93.1,
      hoursSaved: 280,
      approvalsRequired: 360,
      impact: { value: '$612K', label: 'overbilling prevented' },
    },
    ytd: [
      {
        label: 'Invoices checked',
        value: fmtNumber(SPEND_KPIS.invoicesCheckedYtd),
      },
      { label: 'Lines flagged', value: fmtNumber(SPEND_KPIS.linesFlaggedYtd) },
      {
        label: 'Overbilling prevented',
        value: millions(SPEND_KPIS.leakagePreventedYtd),
      },
      {
        label: 'Cash freed up',
        value: millions(SPEND_KPIS.workingCapitalReleasedYtd, 1),
      },
      { label: 'Early-pay discounts', value: '$412K' },
    ],
    where: [
      {
        label: 'Invoice compliance',
        ref: {
          kind: 'page',
          page: 'invoice-compliance',
          label: 'Invoice compliance',
        },
      },
    ],
    guardrails: [
      limit(
        'compliance:short-pay',
        'Hold back payment on disputed lines up to',
        'usd',
        10_000,
        1_000,
        50_000,
        'without approval',
      ),
      hard('compliance:no-bank', 'Never change supplier bank details'),
      rule(
        'compliance:hold-terms',
        'Hold any invoice with a payment-terms violation',
      ),
      hard(
        'compliance:mailbox',
        'Email suppliers only from the ap-disputes@ mailbox',
      ),
      limit(
        'compliance:escalate',
        'Escalate disputes above',
        'usd',
        50_000,
        10_000,
        250_000,
        `to the Accounts Payable Manager, ${getPerson('tom-gallagher').name}`,
      ),
      limit(
        'compliance:early-pay',
        'Take early-payment discounts when they earn a yearly return above',
        'pct',
        10,
        2,
        30,
      ),
    ],
  },
  {
    id: 'sourcing',
    name: 'Sourcing agent',
    short:
      'Drafts bid requests, compares bids on delivered cost, recommends awards',
    description:
      'Drafts request-for-bid packages from category templates, collects and standardizes bids, compares them on delivered cost including tariff and freight, scores suppliers against the weighted criteria and recommends awards. Buyers publish and award.',
    channels: ['email', 'portal'],
    defaultAutonomy: 'approval',
    defaultStatus: 'active',
    sopId: 'PRC-SOP-011',
    metrics: {
      actions: 540,
      acceptancePct: 91.0,
      hoursSaved: 95,
      approvalsRequired: 119,
      impact: { value: '$1.9M', label: 'identified in RFP-2026-014' },
    },
    ytd: [
      { label: 'Competitive bids this year', value: '14' },
      { label: 'Open competitive bids', value: '4' },
      { label: 'Bid comparison', value: '35 min (was 3 days)' },
    ],
    where: [
      {
        label: 'RFP-2026-014',
        ref: { kind: 'sourcing', id: 'RFP-2026-014' },
      },
    ],
    guardrails: [
      hard(
        'sourcing:no-award',
        'Never send a bid request or make an award without buyer approval',
      ),
      rule(
        'sourcing:landed-cost',
        'Compare bids on delivered cost, including tariff, duty and freight',
      ),
      rule(
        'sourcing:risk-flags',
        'Flag bidders with a sanctions, forced-labor import or financial-risk alert',
      ),
      limit(
        'sourcing:single-source',
        'Recommend a second supplier when the supplier risk score is above',
        'num',
        60,
        30,
        90,
      ),
    ],
  },
  {
    id: 'negotiation',
    name: 'Negotiation agent',
    short: 'Email and voice counter-offers on price, terms and warranties',
    description:
      'Prepares briefs and runs email and voice counter-offers on price and on payment terms and warranties, inside the playbook. It discloses that it is an AI assistant on every call and hands over to the buyer when a supplier moves outside the playbook.',
    channels: ['email', 'phone'],
    defaultAutonomy: 'approval',
    defaultStatus: 'shadow',
    shadowNote:
      'Running in practice mode on four pilot categories: counters are drafted and calls are rehearsed, buyers send.',
    sopId: 'PRC-SOP-015',
    metrics: {
      actions: 310,
      acceptancePct: 88.0,
      hoursSaved: 40,
      approvalsRequired: 186,
      impact: { value: '$640K', label: 'secured, 2 payment-term extensions' },
    },
    ytd: [
      { label: 'Negotiations assisted', value: '31' },
      { label: 'Payment terms extended', value: '9' },
      { label: 'Value secured', value: '$2.2M' },
    ],
    where: [
      {
        label: 'Voice negotiation',
        ref: {
          kind: 'page',
          page: 'replenishment',
          label: 'Voice negotiation',
        },
      },
    ],
    guardrails: [
      limit(
        'negotiation:concession',
        'Max concession',
        'pct',
        2,
        0,
        10,
        'below target',
      ),
      hard('negotiation:playbook', 'Never agree to terms outside the playbook'),
      hard(
        'negotiation:disclose',
        'Disclose that it is an AI assistant on every call',
      ),
      rule(
        'negotiation:escalate',
        'Escalate to the category manager when a supplier rejects the second counter',
      ),
    ],
  },
  {
    id: 'followup',
    name: 'Supplier follow-up agent',
    short:
      'PO acknowledgments, delivery dates from emails and portals, rush orders',
    description:
      'Chases PO acknowledgments, reads delivery dates from supplier emails, portals and electronic order confirmations and ship notices, updates promise dates and rushes lines at risk of running out of stock. Escalates slips to the buyer with the days of stock at stake.',
    channels: ['email', 'edi', 'portal', 'phone'],
    defaultAutonomy: 'autonomous',
    defaultStatus: 'active',
    sopId: 'SCM-SOP-007',
    metrics: {
      actions: 3_260,
      acceptancePct: 98.9,
      hoursSaved: 160,
      approvalsRequired: 130,
      impact: { value: '17', label: 'late deliveries caught early' },
    },
    ytd: [
      { label: 'POs tracked', value: '16,420' },
      { label: 'Confirmed electronically', value: '71%' },
      { label: 'Late lines caught early', value: '1,140' },
    ],
    where: [
      {
        label: 'Purchase orders',
        ref: {
          kind: 'page',
          page: 'purchase-orders',
          label: 'Purchase orders',
        },
      },
    ],
    guardrails: [
      limit(
        'followup:chase',
        'Chase unacknowledged POs after',
        'num',
        2,
        1,
        5,
        'business days',
      ),
      limit(
        'followup:expedite',
        'Rush only lines at risk of running out of stock within',
        'num',
        10,
        3,
        21,
        'days of stock',
      ),
      limit(
        'followup:escalate',
        'Escalate to the buyer when a delivery date slips more than',
        'num',
        5,
        1,
        14,
        'days',
      ),
      hard('followup:no-edit', 'Never change PO price or quantity'),
    ],
  },
  {
    id: 'risk',
    name: 'Risk sentinel',
    short: 'Watches quality, credit, sanctions, cyber and regulatory signals',
    description: `Monitors ${fmtNumber(SPEND_KPIS.activeSuppliers)} suppliers across quality systems, credit, sanctions, negative news, cyber, regulatory and weather feeds. Scores exposure, raises alerts with a recommended action and links each one to the affected materials, contracts and competitive bids.`,
    channels: ['qms', 'email', 'teams'],
    defaultAutonomy: 'autonomous',
    defaultStatus: 'active',
    sopId: 'SQA-SOP-030',
    metrics: {
      actions: 2_150,
      acceptancePct: 90.4,
      hoursSaved: 70,
      approvalsRequired: 129,
      impact: { value: '10', label: 'critical and high alerts raised' },
    },
    ytd: [
      {
        label: 'Suppliers monitored',
        value: fmtNumber(SPEND_KPIS.activeSuppliers),
      },
      {
        label: 'Open alerts',
        value: `${SPEND_KPIS.criticalAlerts} critical, ${SPEND_KPIS.highAlerts} high`,
      },
      { label: 'Spend exposed to new tariffs', value: '$186M (4.8%)' },
    ],
    where: [
      {
        label: 'Supplier risk',
        ref: { kind: 'page', page: 'risk', label: 'Supplier risk' },
      },
    ],
    guardrails: [
      rule(
        'risk:notify-critical',
        'Notify the category owner in Teams as soon as a critical alert is raised',
      ),
      limit(
        'risk:credit-drop',
        'Raise an alert when a credit score drops by',
        'num',
        10,
        5,
        25,
        'points or more',
      ),
      rule(
        'risk:scar-draft',
        'Draft corrective action requests for defective product that shipped; Supplier Quality reviews and sends',
      ),
      hard(
        'risk:no-contact',
        'Never contact a supplier without the owner sending it',
      ),
    ],
  },
  {
    id: 'portal',
    name: 'Vendor portal agent',
    short: 'Replays recorded procedures in supplier portals',
    description:
      'Replays recorded procedures in supplier portals: checks order status, downloads invoices and factory test reports, submits releases and acknowledgments, using securely stored credentials. It stops and asks a person when a screen differs from the recording.',
    channels: ['portal'],
    defaultAutonomy: 'autonomous',
    defaultStatus: 'active',
    sopId: 'SCM-SOP-012',
    metrics: {
      actions: 1_480,
      acceptancePct: 99.1,
      hoursSaved: 105,
      approvalsRequired: 74,
      impact: { value: '1,432', label: 'portal tasks completed unattended' },
    },
    ytd: [
      { label: 'Procedures recorded', value: '48' },
      { label: 'Portal sessions', value: '11,900' },
      { label: 'Completed unattended', value: '97%' },
    ],
    where: [
      {
        label: 'Purchase Order Status',
        ref: {
          kind: 'page',
          page: 'purchase-orders',
          label: 'Purchase Order Status',
        },
      },
    ],
    guardrails: [
      hard('portal:recorded-only', 'Replay approved, recorded procedures only'),
      hard(
        'portal:vault',
        'Use securely stored credentials; never display or log a password',
      ),
      rule(
        'portal:stop-unseen',
        'Stop and ask a person on any screen the recording has not seen',
      ),
      hard(
        'portal:no-master-data',
        'Never change bank or tax data in a portal',
      ),
    ],
  },
  {
    id: 'demand',
    name: 'Demand planner',
    short:
      'Explains forecasts, flags unusual months, turns panel bills of materials into component demand',
    description:
      'Reads the statistical forecast for every SKU and site, explains what moved it, flags unusual months and proposes overrides. For the Dallas control panel shop it breaks the finished-panel forecast down through bills of materials into component demand, subtracts stock on hand, open POs and reserve stock, and times it by lead time into orders to place. Planners approve every override and buyers release every PO.',
    channels: ['erp', 'teams'],
    defaultAutonomy: 'approval',
    defaultStatus: 'active',
    sopId: 'SCM-SOP-004',
    metrics: {
      actions: 1_260,
      acceptancePct: 93.5,
      hoursSaved: 90,
      approvalsRequired: 140,
      impact: {
        value: '34',
        label: 'panels flagged at risk from the Vitrex hold',
      },
    },
    ytd: [
      { label: 'Forecasts explained', value: '14,200' },
      { label: 'Unusual months flagged', value: '286' },
      { label: 'Overrides accepted', value: '81%' },
      { label: 'Orders to place drafted', value: '1,540' },
    ],
    where: [
      {
        label: 'Forecast summary',
        ref: {
          kind: 'page',
          page: 'forecast-summary',
          label: 'Forecast summary',
        },
      },
      {
        label: 'Bill of materials',
        ref: {
          kind: 'page',
          page: 'forecast-bom',
          label: 'Bill of materials',
        },
      },
    ],
    guardrails: [
      hard(
        'demand:no-edit',
        'Never change an approved forecast; propose an override for a planner to approve',
      ),
      limit(
        'demand:outlier',
        'Flag a month as unusual when it is more than',
        'num',
        3,
        2,
        5,
        'standard deviations from the typical level',
      ),
      rule(
        'demand:exclusions',
        'Propose excluding one-time project orders from the baseline; a planner approves each exclusion',
      ),
      rule(
        'demand:net-first',
        'Subtract stock on hand, open POs and reserve stock from component demand before drafting an order to place',
      ),
      limit(
        'demand:alternate-window',
        'Propose a qualified backup supplier for at most',
        'num',
        6,
        2,
        12,
        'weeks, then review',
      ),
      hard(
        'demand:buyer-releases',
        'Orders to place go to the replenishment page as drafts; only a buyer releases a PO',
      ),
    ],
  },
  {
    id: 'planner',
    name: 'Replenishment planner',
    short: 'A fixed-rules engine decides; AI explains each suggestion',
    description:
      'The replenishment engine, which follows fixed rules, decides what to buy and when from the approved forecast. The AI layer explains each suggestion in plain language, flags gaps against minimum order value and quantity, and nudges buying ahead and volume prices. Buyers confirm every PO.',
    channels: ['erp'],
    defaultAutonomy: 'suggest',
    defaultStatus: 'active',
    sopId: 'SCM-SOP-001',
    metrics: {
      actions: 730,
      acceptancePct: 95.0,
      hoursSaved: 40,
      approvalsRequired: 15,
      impact: { value: '730', label: 'suggestions explained' },
    },
    ytd: [
      { label: 'Suggestions explained', value: '24,300' },
      { label: 'Confirmed unchanged', value: '95%' },
      { label: 'Buy-ahead savings', value: '$0.8M' },
    ],
    where: [
      {
        label: 'Replenishment',
        ref: { kind: 'page', page: 'buyer-board', label: 'Replenishment' },
      },
    ],
    guardrails: [
      hard(
        'planner:engine-decides',
        'Suggested quantities come from the replenishment engine; AI only explains them',
      ),
      hard(
        'planner:buyer-confirms',
        'Release a PO only after a buyer confirms it',
      ),
      rule(
        'planner:mov-moq',
        'Flag suggestions that fall short of the supplier’s minimum order value or quantity',
      ),
      limit(
        'planner:forward-buy',
        'Suggest buying ahead when a volume price saves more than',
        'pct',
        3,
        1,
        10,
      ),
    ],
  },
];

const AGENTS_BY_ID: Record<AgentId, AgentDef> = {
  director: pick('director'),
  intake: pick('intake'),
  onboarding: pick('onboarding'),
  contracts: pick('contracts'),
  compliance: pick('compliance'),
  sourcing: pick('sourcing'),
  negotiation: pick('negotiation'),
  followup: pick('followup'),
  risk: pick('risk'),
  portal: pick('portal'),
  demand: pick('demand'),
  planner: pick('planner'),
};

function pick(id: AgentId): AgentDef {
  const found = AGENTS.find((agent) => agent.id === id);
  if (!found) {
    throw new Error(`Unknown agent: ${id}`);
  }
  return found;
}

export function getAgent(id: AgentId): AgentDef {
  return AGENTS_BY_ID[id];
}

export const AGENT_NAME: Record<AgentId, string> = {
  director: AGENTS_BY_ID.director.name,
  intake: AGENTS_BY_ID.intake.name,
  onboarding: AGENTS_BY_ID.onboarding.name,
  contracts: AGENTS_BY_ID.contracts.name,
  compliance: AGENTS_BY_ID.compliance.name,
  sourcing: AGENTS_BY_ID.sourcing.name,
  negotiation: AGENTS_BY_ID.negotiation.name,
  followup: AGENTS_BY_ID.followup.name,
  risk: AGENTS_BY_ID.risk.name,
  portal: AGENTS_BY_ID.portal.name,
  demand: AGENTS_BY_ID.demand.name,
  planner: AGENTS_BY_ID.planner.name,
};

export const AGENT_COUNT = AGENT_IDS.length;
